import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  useBlocker,
  useNavigate,
  type BlockerFunction,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { CatalogNavContext } from "./catalogNav";

export const CatalogNavProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDirty, setDirty] = useState(false);
  const skipBlockRef = useRef(false);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (skipBlockRef.current) {
        return false;
      }
      return isDirty && currentLocation.pathname !== nextLocation.pathname;
    },
    [isDirty],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const allowNavigate = useCallback(
    (to: string) => {
      skipBlockRef.current = true;
      setDirty(false);
      navigate(to);
      skipBlockRef.current = false;
    },
    [navigate],
  );

  const contextValue = useMemo(
    () => ({ setDirty, allowNavigate }),
    [allowNavigate],
  );

  return (
    <CatalogNavContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog
        isOpen={blocker.state === "blocked"}
        title={t("catalog.unsavedTitle")}
        message={t("catalog.unsavedMessage")}
        confirmLabel={t("catalog.discard")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={() => {
          setDirty(false);
          blocker.proceed?.();
        }}
        onCancel={() => blocker.reset?.()}
      />
    </CatalogNavContext.Provider>
  );
};
